import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Plus, Send, Edit, Trash2, Search, UserPlus } from 'lucide-react';
import { GROUP_COLORS } from './GroupEditorDialog';
import { Button } from '../../components/ui/button';

/**
 * AdminUsersTab — user list with role/type badges + inline actions.
 *
 * Props:
 *   language, t, users
 *   setFormData / setShowModal / setEditItem — for create/edit modal
 *   openChat / setActiveTab / fetchConversations — "Send message" shortcut
 *   handleDeleteUser
 */
export const AdminUsersTab = ({
  language,
  t,
  users,
  setFormData,
  setShowModal,
  setEditItem,
  openChat,
  setActiveTab,
  fetchConversations,
  handleDeleteUser,
  groups = [], backendUrl, token, fetchGroups, showToast,
}) => {
  const [q, setQ] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [selected, setSelected] = useState([]);
  const [targetGroup, setTargetGroup] = useState('');
  const groupsOf = (id) => groups.filter(g => g.member_ids.includes(id));
  const visible = useMemo(() => {
    const s = q.toLowerCase();
    const g = groups.find(x => x.id === groupFilter);
    return users.filter(u => (!g || g.member_ids.includes(u.id))
      && (!s || [u.full_name, u.username, u.email, u.company_name].some(v => (v || '').toLowerCase().includes(s))));
  }, [users, groups, q, groupFilter]);
  const toggle = (id) => setSelected(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]));
  const allVisibleSelected = visible.length > 0 && visible.every(u => selected.includes(u.id));
  const toggleAll = () => setSelected(allVisibleSelected ? [] : visible.map(u => u.id));
  const addToGroup = async () => {
    if (!targetGroup || !selected.length) return;
    try {
      await axios.post(`${backendUrl}/api/admin/groups/${targetGroup}/members`, { user_ids: selected, action: 'add' }, { headers: { Authorization: `Bearer ${token}` } });
      showToast('success', language === 'it' ? `${selected.length} utenti aggiunti al gruppo` : 'Users added to group');
      setSelected([]);
      fetchGroups();
    } catch {
      showToast('error', 'Errore');
    }
  };
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-white">{language === 'it' ? 'Gestione Utenti' : 'User Management'}</h2>
        <Button
          onClick={() => { setFormData({ role: 'client' }); setShowModal('create-user'); }}
          className="bg-green-600 hover:bg-green-700"
          data-testid="add-user-button"
        >
          <Plus className="w-4 h-4 mr-2" /> {t.newUser}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={language === 'it' ? 'Cerca per nome, email o azienda' : 'Search name, email or company'} className="w-full pl-9 pr-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm" data-testid="users-search" />
        </div>
        <select value={groupFilter} onChange={e => setGroupFilter(e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg text-white text-sm px-3 py-2" data-testid="users-group-filter">
          <option value="">{language === 'it' ? 'Tutti i gruppi' : 'All groups'}</option>
          {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
        {selected.length > 0 && (
          <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-1.5" data-testid="users-bulk-bar">
            <span className="text-sm text-amber-200">{selected.length} {language === 'it' ? 'selezionati' : 'selected'}</span>
            <select value={targetGroup} onChange={e => setTargetGroup(e.target.value)} className="bg-slate-900 border border-slate-700 rounded text-white text-xs px-2 py-1" data-testid="users-bulk-group-select">
              <option value="">{language === 'it' ? 'Scegli gruppo…' : 'Pick group…'}</option>
              {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
            <Button size="sm" onClick={addToGroup} disabled={!targetGroup} className="bg-amber-500 hover:bg-amber-400 text-slate-950 h-7" data-testid="users-bulk-add-btn">
              <UserPlus className="w-3.5 h-3.5 mr-1" /> {language === 'it' ? 'Aggiungi' : 'Add'}
            </Button>
          </div>
        )}
      </div>

      <div className="bg-slate-800 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-700">
            <tr>
              <th className="px-3 py-3 w-8"><input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} className="accent-amber-500" data-testid="users-select-all" /></th>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">{t.username}</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">{language === 'it' ? 'Nome' : 'Name'}</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">{t.email}</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">{t.role}</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">{language === 'it' ? 'Tipo' : 'Type'}</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">{language === 'it' ? 'Gruppi' : 'Groups'}</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-slate-300">{language === 'it' ? 'Azioni' : 'Actions'}</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(u => (
              <tr key={u.id} className="border-t border-slate-700 hover:bg-slate-700/50" data-testid={`user-row-${u.id}`}>
                <td className="px-3 py-3"><input type="checkbox" checked={selected.includes(u.id)} onChange={() => toggle(u.id)} className="accent-amber-500" data-testid={`user-select-${u.id}`} /></td>
                <td className="px-4 py-3 text-white">{u.username}</td>
                <td className="px-4 py-3 text-slate-300">{u.full_name || '-'}</td>
                <td className="px-4 py-3 text-slate-400">{u.email || '-'}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    u.role === 'admin' ? 'bg-red-500/20 text-red-300' :
                    u.role === 'manager' ? 'bg-orange-500/20 text-orange-300' :
                    u.role === 'editor' ? 'bg-purple-500/20 text-purple-300' :
                    u.role === 'collaborator' ? 'bg-cyan-500/20 text-cyan-300' :
                    u.role === 'lead' ? 'bg-yellow-500/20 text-yellow-300' :
                    'bg-blue-500/20 text-blue-300'
                  }`}>
                    {u.role === 'admin' ? '🔐 Admin' :
                     u.role === 'manager' ? '📊 Manager' :
                     u.role === 'editor' ? '✏️ Editor' :
                     u.role === 'collaborator' ? (language === 'it' ? '🤝 Collaboratore' : '🤝 Collaborator') :
                     u.role === 'lead' ? '📝 Lead' :
                     (language === 'it' ? '👤 Cliente' : '👤 Client')}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs text-slate-400">
                    {u.client_type === 'business' ? '🏢' : u.client_type === 'foreign' ? '🌍' : '👤'}{' '}
                    {u.client_type === 'business' ? (language === 'it' ? 'Business' : 'Business') :
                     u.client_type === 'foreign' ? (language === 'it' ? 'Estero' : 'Foreign') :
                     (language === 'it' ? 'Privato' : 'Private')}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {groupsOf(u.id).map(g => (
                      <span key={g.id} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-slate-900 text-slate-200 border border-slate-700">
                        <span className={`w-1.5 h-1.5 rounded-full ${GROUP_COLORS[g.color] || GROUP_COLORS.amber}`} />{g.name}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex gap-1 justify-end">
                    {!['admin', 'manager', 'editor'].includes(u.role) && (
                      <Button
                        onClick={() => { openChat(u.id); setActiveTab('messaging'); fetchConversations(); }}
                        variant="ghost" size="sm"
                        className="text-emerald-400 hover:bg-emerald-500/20"
                        title={language === 'it' ? 'Messaggi' : 'Messages'}
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      onClick={() => { setEditItem(u); setFormData({...u}); setShowModal('edit-user'); }}
                      variant="ghost" size="sm"
                      className="text-blue-400 hover:bg-blue-500/20"
                      data-testid={`edit-user-${u.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    {u.username !== 'admin' && (
                      <Button
                        onClick={() => handleDeleteUser(u.id)}
                        variant="ghost" size="sm"
                        className="text-red-400 hover:bg-red-500/20"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
